import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import authService from '../services/authService';
import { User } from '../types/user';
import '../styles/ProfilePage.css';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Formulaire
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Erreurs de validation
  const [validationErrors, setValidationErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  useEffect(() => {
    const loadUserData = () => {
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      setUser(currentUser);
      setFormData({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        email: currentUser.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setLoading(false);
    };
    
    loadUserData();
  }, [navigate]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Réinitialiser les erreurs de validation lors de la saisie
    setValidationErrors(prev => ({
      ...prev,
      [name]: ''
    }));
    
    // Réinitialiser les messages de succès/erreur
    setSuccess(null);
    setError(null);
  };
  
  const validateForm = () => {
    let isValid = true;
    const errors = {
      firstName: '',
      lastName: '',
      email: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    
    // Validation du prénom
    if (!formData.firstName.trim()) {
      errors.firstName = 'Le prénom est requis';
      isValid = false;
    }
    
    // Validation du nom
    if (!formData.lastName.trim()) {
      errors.lastName = 'Le nom est requis';
      isValid = false;
    }
    
    // Validation de l'email
    if (!formData.email.trim()) {
      errors.email = 'L\'email est requis';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
      isValid = false;
    }
    
    // Validation du mot de passe (uniquement si l'utilisateur souhaite le changer)
    if (formData.newPassword) {
      // Vérifier que le mot de passe actuel est fourni
      if (!formData.currentPassword) {
        errors.currentPassword = 'Le mot de passe actuel est requis pour changer de mot de passe';
        isValid = false;
      }
      
      // Vérifier que le nouveau mot de passe a au moins 6 caractères
      if (formData.newPassword.length < 6) {
        errors.newPassword = 'Le mot de passe doit contenir au moins 6 caractères';
        isValid = false;
      }
      
      // Vérifier que les mots de passe correspondent
      if (formData.newPassword !== formData.confirmPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas';
        isValid = false;
      }
    }
    
    setValidationErrors(errors);
    return isValid;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!user) {
      setError('Utilisateur non connecté');
      return;
    }
    
    setLoading(true);
    
    try {
      // Vérifier le mot de passe actuel si l'utilisateur souhaite changer son mot de passe
      if (formData.newPassword) {
        // Récupérer les données utilisateur actuelles depuis Firestore pour vérifier le mot de passe
        const userRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
          setError('Utilisateur introuvable');
          setLoading(false);
          return;
        }
        
        const userData = userDoc.data();
        
        // Vérifier que le mot de passe actuel est correct
        if (userData.password !== formData.currentPassword) {
          setValidationErrors(prev => ({
            ...prev,
            currentPassword: 'Mot de passe incorrect'
          }));
          setLoading(false);
          return;
        }
      }
      
      // Préparer les données à mettre à jour
      const updateData: Partial<User> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        updatedAt: new Date().toISOString()
      };
      
      // Ajouter le nouveau mot de passe si fourni
      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }
      
      // Mettre à jour l'utilisateur dans Firestore
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, updateData);
      
      // Mettre à jour les informations utilisateur dans le localStorage
      const updatedUser = {
        ...user,
        ...updateData
      };
      
      // Supprimer le mot de passe avant de stocker dans localStorage
      delete updatedUser.password;
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Mettre à jour l'état local
      setUser(updatedUser);
      
      // Réinitialiser les champs de mot de passe
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      setSuccess('Profil mis à jour avec succès');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du profil:', err);
      setError('Erreur lors de la mise à jour du profil. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !user) {
    return (
      <div className="profile-container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }
  
  return (
    <div className="profile-container">
      <h1>Mon Profil</h1>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-group">
          <label htmlFor="firstName">Prénom</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            className={validationErrors.firstName ? 'error' : ''}
          />
          {validationErrors.firstName && (
            <div className="error-text">{validationErrors.firstName}</div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="lastName">Nom</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            className={validationErrors.lastName ? 'error' : ''}
          />
          {validationErrors.lastName && (
            <div className="error-text">{validationErrors.lastName}</div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={validationErrors.email ? 'error' : ''}
          />
          {validationErrors.email && (
            <div className="error-text">{validationErrors.email}</div>
          )}
        </div>
        
        <h2>Changer le mot de passe</h2>
        <p className="password-info">Laissez les champs vides si vous ne souhaitez pas changer votre mot de passe</p>
        
        <div className="form-group">
          <label htmlFor="currentPassword">Mot de passe actuel</label>
          <input
            type="password"
            id="currentPassword"
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleInputChange}
            className={validationErrors.currentPassword ? 'error' : ''}
          />
          {validationErrors.currentPassword && (
            <div className="error-text">{validationErrors.currentPassword}</div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="newPassword">Nouveau mot de passe</label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleInputChange}
            className={validationErrors.newPassword ? 'error' : ''}
          />
          {validationErrors.newPassword && (
            <div className="error-text">{validationErrors.newPassword}</div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className={validationErrors.confirmPassword ? 'error' : ''}
          />
          {validationErrors.confirmPassword && (
            <div className="error-text">{validationErrors.confirmPassword}</div>
          )}
        </div>
        
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Mise à jour...' : 'Enregistrer les modifications'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfilePage;
